import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Card, Checkbox, FormField, Input, PageHeader, Select, Textarea, UnsavedGuard,
} from '../../components/shared';
import { AI_STARTER_DOMAINS } from '../../lib/ai-starters';
import { toast } from '../../components/shared/Toast';
import { AI_MODEL_ALIAS_LABEL, isAiModelAlias, type AiModelAlias } from '@workchat/shared-types';
import { useIsAdminViewer } from '../../lib/menu-visibility';
import {
  createAiAgent, getAiAgent, updateAiAgent, type AiAgentScope,
} from '../../api/assist-agents';
import { useAiChatStore } from '../../store/aiChatStore';

/**
 * 에이전트 만들기 · 고치기 (설계 판 E · 정의서 A4·A5).
 *
 * ## ★★★ 왜 모달이 아니라 «자리» 인가
 *
 * 역할·답변규칙은 여러 줄로 길게 적는 칸이다. 결재 기안이 「560px 모달엔 서식 칸 마흔이 안
 * 들어간다」로 창에서 자리로 나온 그 판단과 같다 — 긴 글을 적는 화면은 주소를 갖는다.
 * 주소를 가지면 **나가기 전에 묻는 규칙**(정본 P13)이 그대로 걸린다.
 *
 * ## ★★★ 다섯 절 중 «넷» — 하나는 아직 지킬 수 없다
 *
 * 정의서 A4 는 다섯 절(기본정보 / 검색 데이터 / 배경 지식 / 역할·답변규칙 / 권한)을 적었다.
 * · **검색 데이터(도메인)** — 판 E-② 에서 길이 났다(`tool_domains` 가 chat-server →
 *   gateway-ai → orchestrator 를 통과한다). 그래서 이제 «그린다».
 * · **배경 지식 파일** — 올린 파일을 읽어 답에 싣는 곳이 아직 없다 → 안 그린다.
 * · **권한** — 도구가 «부른 사람 명의»로만 도니 에이전트가 정할 것이 없다 → 절 자체가 없다.
 * 그려 두면 「정했는데 아무 일도 안 일어난다」가 된다 — 길이 열린 것만 그린다.
 */

/** 폼 한 벌 — 「사람이 고쳤나」를 이 모양 그대로 비교한다. */
interface Draft {
  name: string;
  description: string;
  roleInstructions: string;
  answerRules: string;
  modelAlias: AiModelAlias | null;
  scope: AiAgentScope;
  starters: [string, string, string];
  /** 뒤질 도메인 — **빈 목록 = 전부**. */
  toolDomains: string[];
}

const EMPTY: Draft = {
  name: '', description: '', roleInstructions: '', answerRules: '',
  modelAlias: null, scope: 'PERSONAL', starters: ['', '', ''], toolDomains: [],
};

const ROLE_MAX = 2_000;

export function AiAgentFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAdmin = useIsAdminViewer();
  const isNew = !id || id === 'new';

  const [draft, setDraft] = useState<Draft>(EMPTY);
  /** 처음 실린 값 — 「사람이 고쳤나」의 기준이다. */
  const [initial, setInitial] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  /* ★ 상태가 아니라 ref 로 두 번 저장을 막는다 — 같은 프레임의 두 번째 클릭은 상태로 못 잡는다
       (결재 기안이 10초에 문서 8건을 만든 자리). */
  const savingRef = useRef(false);

  const modelAliases = useAiChatStore((s) => s.modelAliases);
  const loadModels = useAiChatStore((s) => s.loadModels);
  useEffect(() => { void loadModels(); }, [loadModels]);

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    void (async () => {
      try {
        const a = await getAiAgent(id!);
        if (!alive) return;
        const next: Draft = {
          name: a.name,
          description: a.description,
          roleInstructions: a.roleInstructions,
          answerRules: a.answerRules,
          modelAlias: isAiModelAlias(a.modelAlias) ? a.modelAlias : null,
          scope: a.scope,
          starters: [a.starters[0] ?? '', a.starters[1] ?? '', a.starters[2] ?? ''],
          toolDomains: a.toolDomains ?? [],
        };
        setDraft(next);
        setInitial(next);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew]);

  /**
   * ★★★ **「비어 있지 않다」가 아니라 「사람이 고쳤다」**(정본 P13). 새로 열자마자 뜨는 창은
   *    사람이 읽지 않고 누르게 만든다 — 그러면 정작 잃을 것이 있을 때도 그냥 누른다.
   */
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial],
  );

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setStarter = (i: number, v: string) =>
    setDraft((d) => {
      const s: [string, string, string] = [...d.starters];
      s[i] = v;
      return { ...d, starters: s };
    });

  async function save(): Promise<void> {
    if (savingRef.current) return;
    const name = draft.name.trim();
    if (!name) {
      toast.error('이름을 입력해 주세요.');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        name,
        description: draft.description.trim(),
        roleInstructions: draft.roleInstructions.trim(),
        answerRules: draft.answerRules.trim(),
        modelAlias: draft.modelAlias,
        starters: draft.starters.map((s) => s.trim()).filter(Boolean),
        toolDomains: draft.toolDomains,
      };
      if (isNew) await createAiAgent({ ...payload, scope: draft.scope });
      else await updateAiAgent(id!, payload);
      /* ★ 저장 성공한 «한 곳»에서 이탈 경고를 끈다 — 안 끄면 「저장하고 목록으로」가 창 뒤에서 멈춘다. */
      setInitial(draft);
      toast.success(isNew ? '에이전트를 만들었습니다.' : '저장했습니다.');
      navigate('/ai/agents');
    } catch {
      toast.error('저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  if (notFound) {
    return (
      <div className="aiagents">
        <PageHeader breadcrumb={['AI', '에이전트']} title="에이전트" />
        <div className="aiagents__col">
          <p className="aiagents__note" role="alert">
            에이전트를 찾을 수 없습니다. 중지되었거나 고칠 수 있는 권한이 없습니다.
          </p>
          <div>
            <Button variant="secondary" onClick={() => navigate('/ai/agents')}>목록으로</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aiagents">
      <UnsavedGuard when={dirty && !saving} />
      <PageHeader
        breadcrumb={['AI', '에이전트', isNew ? '새 에이전트' : '수정']}
        title={isNew ? '새 에이전트' : '에이전트 수정'}
        description="역할과 답변 규칙을 적어 두면, 이 에이전트로 묻는 모든 대화가 그대로 따릅니다."
      />

      <div className="aiagents__col">
        {loading ? (
          <p className="aiagents__note">불러오는 중입니다…</p>
        ) : (
          <form
            className="aiform"
            onSubmit={(e) => { e.preventDefault(); void save(); }}
          >
            {/* 절마다 정본 D9 Card — 제목이 곧 카드 머리다. */}
            <Card title="기본 정보" className="aiform__sec">

              <FormField label="이름" required counter={{ value: draft.name.length, max: 60 }}>
                <Input
                  value={draft.name}
                  maxLength={60}
                  fullWidth
                  placeholder="예: 영업 도우미"
                  onChange={(e) => set('name', e.target.value)}
                />
              </FormField>

              <FormField label="설명" hint="목록에서 무엇을 하는 에이전트인지 알아보는 한 줄입니다.">
                <Input
                  value={draft.description}
                  maxLength={200}
                  fullWidth
                  placeholder="예: 견적과 계약 관련 자료를 찾아 정리합니다"
                  onChange={(e) => set('description', e.target.value)}
                />
              </FormField>

              {/* 범위는 «만들 때»만 정한다 — 뒤집을 방법이 없으므로 고치기 화면에는 안 그린다. */}
              {isNew && isAdmin && (
                <FormField
                  label="공개 범위"
                  hint="전사로 만들면 회사의 모든 사람이 이 에이전트로 물을 수 있습니다. 만든 뒤에는 바꿀 수 없습니다."
                >
                  <Select
                    value={draft.scope}
                    onChange={(v) => set('scope', v as AiAgentScope)}
                    options={[
                      { value: 'PERSONAL', label: '나만' },
                      { value: 'TENANT', label: '회사 전체' },
                    ]}
                  />
                </FormField>
              )}

              {/* 고를 수 있는 것이 둘 이상일 때만 그린다 — 하나뿐이면 고르는 것이 아니다(관문 G2). */}
              {modelAliases.length > 1 && (
                <FormField label="답변 방식" hint="안 고르면 이 회사의 기본 설정을 따릅니다.">
                  <Select
                    value={draft.modelAlias ?? ''}
                    onChange={(v) => set('modelAlias', isAiModelAlias(v) ? v : null)}
                    options={[
                      { value: '', label: '기본' },
                      ...modelAliases.map((a) => ({ value: a, label: AI_MODEL_ALIAS_LABEL[a].label })),
                    ]}
                  />
                </FormField>
              )}
            </Card>

            {/* 절마다 정본 D9 Card — 제목이 곧 카드 머리다. */}
            <Card title="역할 · 답변 규칙" className="aiform__sec">

              <FormField
                label="역할"
                hint="「너는 무엇을 하는 사람인가」를 적습니다."
                counter={{ value: draft.roleInstructions.length, max: ROLE_MAX }}
              >
                <Textarea
                  value={draft.roleInstructions}
                  rows={5}
                  maxLength={ROLE_MAX}
                  fullWidth
                  placeholder="예: 너는 영업팀의 업무를 돕는다. 견적·계약·고객 문의를 다룬다."
                  onChange={(e) => set('roleInstructions', e.target.value)}
                />
              </FormField>

              <FormField
                label="답변 규칙"
                hint="말투 · 길이 · 출처 표시처럼 «어떻게 답하는가»를 적습니다."
                counter={{ value: draft.answerRules.length, max: ROLE_MAX }}
              >
                <Textarea
                  value={draft.answerRules}
                  rows={5}
                  maxLength={ROLE_MAX}
                  fullWidth
                  placeholder={'예: 세 줄 안으로 답한다. 근거가 된 문서 이름을 함께 적는다.'}
                  onChange={(e) => set('answerRules', e.target.value)}
                />
              </FormField>
            </Card>

            {/* 절마다 정본 D9 Card — 제목이 곧 카드 머리다. */}
            {/*
              ★★★ **「권한」이 아니다.** 도구는 언제나 부른 사람 명의로 도니(ai-actor), 좁혀도
                 그 사람이 볼 수 있는 것은 그대로다. 곁말이 그 사실을 먼저 말한다 — 안 그러면
                 사용자는 이 체크박스를 «접근 제어»로 읽는다.
              ★★ **아무것도 안 고르면 전부**다. 「전체」 체크박스를 따로 두지 않는 이유 —
                 상태가 둘(전체 · 고름)로 갈리면 「전체인데 메일만 체크됨」 같은 자리가 생긴다.
            */}
            <Card title="검색 데이터" className="aiform__sec">
              <p className="aiform__note">
                이 에이전트가 어디를 뒤질지 정합니다. 아무것도 고르지 않으면 전부 찾아봅니다.
                <br />
                볼 수 있는 자료의 범위는 바뀌지 않습니다 — 언제나 <b>묻는 사람의 권한</b>을 따릅니다.
              </p>
              <div className="aiform__doms">
                {AI_STARTER_DOMAINS.map((d) => {
                  const on = draft.toolDomains.includes(d.key);
                  return (
                    <Checkbox
                      key={d.key}
                      checked={on}
                      label={d.label}
                      onChange={() =>
                        set(
                          'toolDomains',
                          on
                            ? draft.toolDomains.filter((k) => k !== d.key)
                            : [...draft.toolDomains, d.key],
                        )
                      }
                    />
                  );
                })}
              </div>
            </Card>

            <Card title="시작 질문" className="aiform__sec">
              <p className="aiform__note">
                이 에이전트로 대화를 열었을 때 보여 줄 예시입니다. 비워 두면 안 보입니다.
              </p>
              {[0, 1, 2].map((i) => (
                <FormField key={i} label={`시작 질문 ${i + 1}`}>
                  <Input
                    value={draft.starters[i] ?? ''}
                    maxLength={120}
                    fullWidth
                    onChange={(e) => setStarter(i, e.target.value)}
                  />
                </FormField>
              ))}
            </Card>

            <div className="aiform__acts">
              <Button type="submit" variant="primary" loading={saving}>
                {isNew ? '만들기' : '저장'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate('/ai/agents')}>
                취소
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
